import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { ArrowUp, ArrowDown } from 'icons';

import { Heading, Box, SvgIcon } from 'components';
import {
  DfmSummaryItem,
  DfmFeatureProps,
} from 'apps/rfq/rfqPage/views/DFMIssues/DfmSummary/DfmSummaryItem';

interface DfmReportProps {
  keys(): number[];
  get(key: number): DfmFeatureProps;
}

// wasm [*DfmAnalysis] obejct
interface DfmProps {
  GetDfmReport(): DfmReportProps; // std::map<int, DfmAnalysisReport>
}

// wasm [*Body] object
interface BodyProps {
  isReadyForDfmCutoff: boolean;
  GetDfm(): DfmProps;
}
interface DfmSummaryProps {
  body: BodyProps;
  processId?: string;
  units: string;
  title: string;
  bold?: boolean;
  defaultExpanded?: boolean;
}

const baseStyles = {
  alignItems: 'center',
  display: 'flex',
  userSelect: 'none',
  width: '100%',
  cursor: 'pointer',
};

export const DfmSummary = observer(
  ({
    body,
    processId,
    units,
    title,
    bold,
    defaultExpanded,
  }: DfmSummaryProps) => {
    // expanded/collapsed state
    const [isCollapsed, setIsCollapsed] = !defaultExpanded
      ? useState(true)
      : useState(false);

    if (!body) return null;
    if (!body.isReadyForDfmCutoff) return null;

    // get the Body DFM feature list
    const featureList = [];
    const dfmReportObj = body.GetDfm()?.GetDfmReport();
    const featKeys = dfmReportObj?.keys();
    // eslint-disable-next-line no-restricted-syntax
    for (const featKey of featKeys) {
      const featObj = dfmReportObj?.get(featKey);
      featureList.push({
        featKey,
        featObj,
      });
    }

    const handleToggle = () => {
      setIsCollapsed(prevState => !prevState);
    };

    return (
      <Box>
        <Box sx={{ ...baseStyles }}>
          {/* title */}
          <Heading
            level={6}
            size='default'
            weight={bold ? 'xbold' : 'regular'}
            margin='0 8px 0 0'
            onClick={handleToggle}
          >
            {title}
          </Heading>
          <SvgIcon
            icon={isCollapsed ? <ArrowDown /> : <ArrowUp />}
            size='medium'
          />
        </Box>

        {/* collapsable list of DFM features */}
        {!isCollapsed && (
          <>
            <Box mt={1}>
              {featureList.map((dfmFeat, ix) => (
                <DfmSummaryItem
                  key={ix}
                  processId={processId}
                  units={units}
                  dfmFeatObj={dfmFeat.featObj}
                  dfmFeatType={dfmFeat.featKey}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  },
);
